/**
 * PDF drift note: `django/document_generator/proposal_v2_sdk_data.py` temporarily mirrors this file
 * (and `parseDataToState.js`) so Proposal V2 URL PDF generation can build the same
 * `ProposalDataTypeV2` shape server-side. When you change logic here, update
 * `proposal_v2_sdk_data.py` too.
 */
import {
  getDefaultProposalV2NonSolarConfig,
  mergeTemplateSettingsIntoProposalV2Config,
} from 'resources/proposalTemplates/proposalV2/getProposalV2Config'
import { resolveProposalDisplayLocale } from 'resources/proposalTemplates/proposalV2/resolveProposalDisplayLocale'
import {
  isProposalV2Type,
  ProposalTemplateTypeEnum,
  resolveProposalTemplateType,
} from 'resources/proposalTemplates/types'
import { isQuickQuote } from 'util/quoteMode'
import { currencySymbolForCountry, getTemplateSettings } from '../util/misc'
import { getIsPhoenixV2 } from './elements/phoenix/util'
import { resolveQuoteSystemForProposal } from './resolveQuoteSystemForProposal'

const DEFAULT_Y_AXIS_SCALING_MARGIN_FACTOR = 1.1

const isQuickQuoteProject = (selectedProject) => isQuickQuote(selectedProject?.quote_mode)

/** Mirror quote wire onto `proposal_data` so the non-solar iframe can read it when system userData lags. */
const enrichProjectProposalDataWithQuoteLineItems = (selectedProject, quoteLineItems) => {
  if (!quoteLineItems || !selectedProject) return selectedProject
  return {
    ...selectedProject,
    proposal_data: {
      ...(selectedProject.proposal_data || {}),
      quote_line_items: quoteLineItems,
    },
  }
}

/** Design rows win; proposal ``userData`` cache only when design not hydrated yet; always an array. */
const pickQuoteLineItemsForProposalMerge = (designLineItems, cacheLineItems) => {
  if (Array.isArray(designLineItems) && designLineItems.length > 0) return designLineItems
  if (Array.isArray(cacheLineItems) && cacheLineItems.length > 0) return cacheLineItems
  return []
}

const PAYMENT_OPTION_CONTRACT_FIELDS = [
  'contract_template',
  'contract_base_pdf_url',
  'contract_template_id',
  'contract_pdf_content_data',
]

const findProposalPaymentOptionById = (selectedProject, paymentOptionId) => {
  if (selectedProject == null || paymentOptionId == null) return null
  const systems = selectedProject.proposal_data?.systems ?? []
  for (const system of systems) {
    const paymentOptions = system?.data?.payment_options ?? []
    const match = paymentOptions.find((po) => po?.id === paymentOptionId)
    if (match) return match
  }
  return null
}

const readNonsolarContractTemplateIdFromSettings = (selectedProject, paymentOptionId, proposalTemplateSettings) => {
  const templateSettings =
    proposalTemplateSettings?.proposal_v2_config?.template_settings ??
    selectedProject?.proposal_data?.proposal_template_settings?.proposal_v2_config?.template_settings ??
    selectedProject?.proposal_template_settings?.proposal_v2_config?.template_settings
  const contractSettings = templateSettings?.contract
  if (!contractSettings?.enabled) return null

  if (contractSettings.scope === 'perSystemPaymentOption' && paymentOptionId != null) {
    const perPayment = contractSettings.contractByPaymentOptionId ?? {}
    const templateId = perPayment[String(paymentOptionId)] ?? perPayment[paymentOptionId]
    return templateId != null && templateId !== '' ? Number(templateId) : null
  }

  const templateId = contractSettings.templateId
  return templateId != null && templateId !== '' ? Number(templateId) : null
}

/** Quick-quote checkout reads payment options from Studio design; merge contract metadata from proposal_data. */
const mergeContractFieldsFromProposalPaymentOption = (
  paymentOption,
  proposalPaymentOption,
  selectedProject,
  proposalTemplateSettings
) => {
  if (!paymentOption) return paymentOption
  const merged = proposalPaymentOption ? { ...paymentOption } : { ...paymentOption }

  if (proposalPaymentOption) {
    for (const field of PAYMENT_OPTION_CONTRACT_FIELDS) {
      const proposalValue = proposalPaymentOption[field]
      if (proposalValue != null && proposalValue !== '' && (merged[field] == null || merged[field] === '')) {
        merged[field] = proposalValue
      }
    }
  }

  const templateSettings =
    proposalTemplateSettings?.proposal_v2_config?.template_settings ??
    selectedProject?.proposal_data?.proposal_template_settings?.proposal_v2_config?.template_settings ??
    selectedProject?.proposal_template_settings?.proposal_v2_config?.template_settings
  const contractSettings = templateSettings?.contract

  if (proposalTemplateSettings && contractSettings) {
    // Customize Quote drawer / live SDK payload — template settings override stale proposal_data.
    if (!contractSettings.enabled) {
      merged.contract_template_id = null
      delete merged.contract_template
      delete merged.contract_base_pdf_url
      delete merged.contract_pdf_content_data
    } else {
      const templateId = readNonsolarContractTemplateIdFromSettings(
        selectedProject,
        merged.id,
        proposalTemplateSettings
      )
      merged.contract_template_id = templateId ?? null
      if (templateId == null) {
        delete merged.contract_template
        delete merged.contract_base_pdf_url
        delete merged.contract_pdf_content_data
      }
    }
  } else if (merged.contract_template_id == null && selectedProject) {
    const templateId = readNonsolarContractTemplateIdFromSettings(selectedProject, merged.id, proposalTemplateSettings)
    if (templateId != null) {
      merged.contract_template_id = templateId
    }
  }

  return merged
}

const enrichPaymentOptionsWithProposalContractFields = (paymentOptions, selectedProject, proposalTemplateSettings) => {
  if (!Array.isArray(paymentOptions) || !paymentOptions.length || !isQuickQuoteProject(selectedProject)) {
    return paymentOptions
  }
  return paymentOptions.map((po) => {
    const fromProposal = findProposalPaymentOptionById(selectedProject, po?.id)
    return mergeContractFieldsFromProposalPaymentOption(po, fromProposal, selectedProject, proposalTemplateSettings)
  })
}

/** Copy contract metadata from ``proposal_data.systems`` onto the checkout payment option. */
const enrichPaymentOptionWithProposalContractFields = (paymentOption, selectedProject, proposalTemplateSettings) => {
  if (!paymentOption || !isQuickQuoteProject(selectedProject)) return paymentOption
  const fromProposal = findProposalPaymentOptionById(selectedProject, paymentOption.id)
  return mergeContractFieldsFromProposalPaymentOption(
    paymentOption,
    fromProposal,
    selectedProject,
    proposalTemplateSettings
  )
}

/**
 * Merge unsaved Customize Quote drawer settings into the SDK payload for the
 * non-solar iframe (contract, cover image, language, case studies) and re-apply
 * contract template ids onto payment options for checkout.
 */
export const buildNonSolarSdkProposalData = (proposalData, unsavedTemplateSettings) => {
  if (!proposalData?.selectedProject || !isQuickQuoteProject(proposalData.selectedProject)) {
    return proposalData
  }
  if (proposalData.proposalTemplateSettings?.type !== ProposalTemplateTypeEnum.proposalV2_nonsolar) {
    return proposalData
  }

  const proposal_v2_config = proposalData.proposalTemplateSettings?.proposal_v2_config
  if (!proposal_v2_config) {
    return proposalData
  }

  const mergedConfig = unsavedTemplateSettings
    ? mergeTemplateSettingsIntoProposalV2Config(proposal_v2_config, unsavedTemplateSettings)
    : proposal_v2_config

  const effectiveProposalTemplateSettings = {
    ...proposalData.proposalTemplateSettings,
    proposal_v2_config: mergedConfig,
  }

  const selectedProject = proposalData.selectedProject
  const enrichedSelectedProject = {
    ...selectedProject,
    proposal_template_settings: effectiveProposalTemplateSettings,
    proposal_data: selectedProject.proposal_data
      ? {
          ...selectedProject.proposal_data,
          proposal_template_settings: effectiveProposalTemplateSettings,
        }
      : selectedProject.proposal_data,
  }

  let selectedPaymentOption = enrichPaymentOptionWithProposalContractFields(
    proposalData.selectedPaymentOption,
    enrichedSelectedProject,
    effectiveProposalTemplateSettings
  )

  const enrichSystem = (sys) =>
    sys
      ? {
          ...sys,
          payment_options: enrichPaymentOptionsWithProposalContractFields(
            sys.payment_options,
            enrichedSelectedProject,
            effectiveProposalTemplateSettings
          ),
        }
      : sys

  const selectedSystem = enrichSystem(proposalData.selectedSystem)
  const systems = (proposalData.systems || []).map(enrichSystem)

  if (selectedPaymentOption?.id && selectedSystem?.payment_options?.length) {
    const match = selectedSystem.payment_options.find((po) => po?.id === selectedPaymentOption.id)
    if (match) {
      selectedPaymentOption = match
    }
  }

  const templateLanguage = mergedConfig.template_settings?.language
  const locale = resolveProposalDisplayLocale({
    templateLanguage,
    projectLanguage: enrichedSelectedProject?.language_override || enrichedSelectedProject?.language,
  })

  return {
    ...proposalData,
    locale,
    proposalTemplateSettings: effectiveProposalTemplateSettings,
    selectedProject: enrichedSelectedProject,
    selectedPaymentOption,
    selectedSystem,
    systems,
  }
}

/** Copy incentive/discount fields from nested `pricing` onto the PO wire shape the iframe reads. */
const hydratePaymentOptionForProposal = (paymentOption) => {
  if (!paymentOption) return paymentOption
  const pricing = paymentOption.pricing || {}
  const incentive =
    paymentOption.incentive_to_customer?.total != null && paymentOption.incentive_to_customer.total !== 0
      ? paymentOption.incentive_to_customer
      : pricing.incentive_to_customer || paymentOption.incentive_to_customer
  const priceAdjustments =
    paymentOption.price_adjustments?.total != null && paymentOption.price_adjustments.total !== 0
      ? paymentOption.price_adjustments
      : pricing.price_adjustments || paymentOption.price_adjustments
  const topLine =
    paymentOption.top_line_system_price > 0
      ? paymentOption.top_line_system_price
      : (pricing.top_line_system_price ?? pricing.system_price_including_tax ?? paymentOption.top_line_system_price)

  return {
    ...paymentOption,
    top_line_system_price: topLine,
    incentive_to_customer: incentive,
    price_adjustments: priceAdjustments,
    net_cost_to_customer: paymentOption.net_cost_to_customer ?? pricing.system_price_payable,
  }
}

/**
 * `user_logins` systems often lack Studio `userData.line_items`; merge quote rows from
 * `project.design` so the non-solar iframe quotation table matches the Quote tab.
 */
/** Ensure quote rows live under `userData` for the non-solar iframe (`resolveLineItems`). */
const ensureNonSolarSystemHasQuoteUserData = (sys) => {
  if (!sys) return sys

  const topLevelUd = {
    quote_line_items: sys.quote_line_items,
    line_items: sys.line_items,
    incentive_to_installer: sys.incentive_to_installer,
  }
  const hasTopLevelQuote =
    topLevelUd.quote_line_items != null ||
    (Array.isArray(topLevelUd.line_items) && topLevelUd.line_items.length > 0) ||
    (Array.isArray(topLevelUd.incentive_to_installer) && topLevelUd.incentive_to_installer.length > 0)

  if (!hasTopLevelQuote) return sys

  const existingUd = sys.userData && typeof sys.userData === 'object' ? { ...sys.userData } : {}
  const mergedUd = {
    ...topLevelUd,
    ...existingUd,
    quote_line_items: existingUd.quote_line_items ?? topLevelUd.quote_line_items,
    line_items: existingUd.line_items?.length ? existingUd.line_items : topLevelUd.line_items,
    incentive_to_installer: existingUd.incentive_to_installer ?? topLevelUd.incentive_to_installer,
  }

  return { ...sys, userData: mergedUd }
}

const mergeNonSolarQuoteSystemIntoProposal = ({ selectedProject, selectedSystem, systems }) => {
  if (!isQuickQuoteProject(selectedProject)) {
    return { selectedProject, selectedSystem, systems }
  }

  const fromDesign = resolveQuoteSystemForProposal({ selectedProject })
  if (!fromDesign?.uuid) {
    const normalizedSelected = ensureNonSolarSystemHasQuoteUserData(selectedSystem)
    const normalizedSystems = (systems || []).map(ensureNonSolarSystemHasQuoteUserData)
    const quoteWire = normalizedSelected?.userData?.quote_line_items
    const enrichedProject = enrichProjectProposalDataWithQuoteLineItems(selectedProject, quoteWire)

    return {
      selectedProject: enrichedProject,
      selectedSystem: normalizedSelected,
      systems: normalizedSystems,
    }
  }

  const designUd = fromDesign.userData && typeof fromDesign.userData === 'object' ? fromDesign.userData : {}
  const quoteWire = designUd.quote_line_items

  const mergeOneSystem = (sys) => {
    if (!sys || sys.uuid !== fromDesign.uuid) return ensureNonSolarSystemHasQuoteUserData(sys)

    const existingUd = sys.userData && typeof sys.userData === 'object' ? { ...sys.userData } : {}
    const mergedLineItems = pickQuoteLineItemsForProposalMerge(designUd.line_items, existingUd.line_items)
    const mergedUd = {
      ...existingUd,
      ...designUd,
      quote_line_items: quoteWire || designUd.quote_line_items || existingUd.quote_line_items,
      line_items: mergedLineItems,
      incentive_to_installer: designUd.incentive_to_installer ?? existingUd.incentive_to_installer,
    }

    return ensureNonSolarSystemHasQuoteUserData({
      ...sys,
      userData: mergedUd,
      payment_options: enrichPaymentOptionsWithProposalContractFields(
        sys.payment_options?.length ? sys.payment_options : fromDesign.payment_options,
        selectedProject,
        selectedProject?.proposal_template_settings ?? selectedProject?.proposal_data?.proposal_template_settings
      ),
      pricing: sys.pricing ?? fromDesign.pricing,
    })
  }

  let mergedSelected = selectedSystem ? mergeOneSystem(selectedSystem) : fromDesign
  if (!mergedSelected) mergedSelected = fromDesign
  if (mergedSelected?.payment_options) {
    mergedSelected = {
      ...mergedSelected,
      payment_options: enrichPaymentOptionsWithProposalContractFields(
        mergedSelected.payment_options,
        selectedProject,
        selectedProject?.proposal_template_settings ?? selectedProject?.proposal_data?.proposal_template_settings
      ),
    }
  }

  const mergedSystems = (systems || []).map(mergeOneSystem)
  if (!mergedSystems.some((s) => s?.uuid === fromDesign.uuid)) {
    mergedSystems.push(fromDesign)
  }

  const enrichedProject = enrichProjectProposalDataWithQuoteLineItems(selectedProject, quoteWire)

  return {
    selectedProject: enrichedProject,
    selectedSystem: mergedSelected,
    systems: mergedSystems.length ? mergedSystems : [fromDesign],
  }
}

/**
 * Force Non-Solar iframe template when job type is non-solar but the org/project
 * template is still solar-oriented (classic opensolar or Sales Machine grapes_swipe).
 * bespoke V2 and proposalV2_nonsolar are left unchanged.
 */
const buildEffectiveProposalTemplateSettings = (proposalTemplateSettings, selectedProject) => {
  if (!isQuickQuoteProject(selectedProject)) {
    return proposalTemplateSettings
  }
  const resolved = resolveProposalTemplateType(proposalTemplateSettings)
  if (resolved === ProposalTemplateTypeEnum.proposalV2_nonsolar) {
    return proposalTemplateSettings
  }
  const isClassicOrSalesMachine =
    !isProposalV2Type(resolved) || resolved === ProposalTemplateTypeEnum.proposalV2_grapes_swipe
  if (!isClassicOrSalesMachine) {
    return proposalTemplateSettings
  }

  const nonSolarDefaults = getDefaultProposalV2NonSolarConfig()
  return {
    ...proposalTemplateSettings,
    type: ProposalTemplateTypeEnum.proposalV2_nonsolar,
    // Legacy `sections` (e.g. letter + systems_group) must not drive chrome for V2 iframe.
    sections: '',
    proposal_v2_config: {
      ...nonSolarDefaults,
      ...(proposalTemplateSettings?.proposal_v2_config || {}),
      type: ProposalTemplateTypeEnum.proposalV2_nonsolar,
      view_config: {
        ...nonSolarDefaults.view_config,
        ...(proposalTemplateSettings?.proposal_v2_config?.view_config || {}),
        pathname: 'non-solar',
      },
      template_settings: {
        ...nonSolarDefaults.template_settings,
        ...(proposalTemplateSettings?.proposal_v2_config?.template_settings || {}),
      },
    },
  }
}

const getPaymentOptionById = (id, paymentOptions, fallbackToFirst) => {
  const matches = paymentOptions.filter((paymentOption) => paymentOption.id === id)
  if (matches.length > 0) {
    return matches[0]
  } else if (fallbackToFirst === true) {
    return paymentOptions[0]
  } else {
    return null
  }
}

const getCashFlowAxisRangeAllSystemsAndPaymentOptions = (
  systems,
  valueKey,
  YAxisMarginFactor = DEFAULT_Y_AXIS_SCALING_MARGIN_FACTOR
) => {
  const getMaxOrMin = (func) =>
    func.apply(
      null,
      systems.map((s) =>
        func.apply(
          null,
          s.payment_options.map((p) =>
            func.apply(
              null,
              p[valueKey].map((v) => v)
            )
          )
        )
      )
    )

  const maxValue = getMaxOrMin(Math.max)
  const minValue = getMaxOrMin(Math.min)

  // We need to make sure we create some padding on the margins for both upper and lower YAxis on a chart.
  // This is required to ensure any chart labels and text do not get cut off.
  const factorIncreaseYAxisMargin = YAxisMarginFactor - 1
  const marginAdjustment = Math.max(Math.abs(maxValue), Math.abs(minValue)) * factorIncreaseYAxisMargin

  return {
    maxYAxis: maxValue + marginAdjustment,
    minYAxis: minValue - marginAdjustment,
  }
}

const getBillSavingsAxisRangeAllSystemsAndPaymentOptions = (systems) => {
  // Previously we only needed to iterate over systems, not payment options because no impact due to payment option
  // But now the regular_payment type impacts this chart so we need to iterate over payment options too.
  const getMaxOrMin = (func, key) =>
    func.apply(
      null,
      systems.map((s) =>
        func.apply(
          null,
          s.payment_options.map((po) =>
            func.apply(
              null,
              Object.values(s.bills.proposed).map(
                (proposed) =>
                  window.Designer.LifeTimeSavingsMaxMagnitudeY(
                    s.bills.current.bills_yearly,
                    proposed.bills_yearly,
                    po.payment_type === 'regular_payment' ? po.cash_flows_payments.slice(1) : null
                  )[key]
              )
            )
          )
        )
      )
    )

  return {
    maxYAxis: getMaxOrMin(Math.max, 'maxYAxis'),
    minYAxis: getMaxOrMin(Math.min, 'minYAxis'),
  }
}

export default function prepareProposalData(props) {
  const {
    proposalTemplateSettings,
    org,
    selectedProject,
    systems,
    selectedSystem,
    otherDesigns,
    otherAccounts,
    viewUuids,
    selectedViewUuid,
    selectedPaymentOptionId,
    myeStyles,
    fullCalcReady,
    changeOrderProposal,
    queryParams,
  } = props

  // Allow preparing other aspects of proposal data even if selectedSystem is not populated But only if allowEmptySystem
  // is set to true. This is only used when facetMode is enabled so scenarios that do not support facet drawing will be
  // unchanged.
  const allowEmptySystem = ['force', 'allow'].includes(queryParams?.facetMode)

  // Proposal V2 templates (Sales Machine `proposalV2_grapes_swipe` + Non-Solar) render in
  // an iframe driven by `sdk.proposal.data`. They do not require a Studio-backed
  // `selectedSystem` in `parseDataToState` (placeholder / design data may omit systems).
  const resolvedTemplateType = resolveProposalTemplateType(proposalTemplateSettings)
  const allowEmptySystemForProposalV2 = isProposalV2Type(resolvedTemplateType)
  const allowEmptySystemForNonSolar = isQuickQuoteProject(selectedProject)
  const allowEmptySystemForIframeProposal = allowEmptySystemForProposalV2 || allowEmptySystemForNonSolar

  if (!selectedSystem && allowEmptySystem !== true && !allowEmptySystemForIframeProposal) {
    return null
  }

  if (!selectedSystem && allowEmptySystemForIframeProposal) {
    // Return a minimal-but-valid proposalData object so BasicLayout / ProposalV2 can
    // mount. Hydrate the quote placeholder from project.design / Studio when
    // `proposal_data.systems` is still empty (common for non-solar before full calc).
    const quotePlaceholderSystem = resolveQuoteSystemForProposal({ selectedProject })
    const hydratedSelectedSystem = quotePlaceholderSystem || undefined
    const hydratedSystems = systems?.length > 0 ? systems : quotePlaceholderSystem ? [quotePlaceholderSystem] : []

    let hydratedPaymentOption = null
    if (hydratedSelectedSystem?.payment_options?.length) {
      hydratedPaymentOption = getPaymentOptionById(
        selectedPaymentOptionId,
        hydratedSelectedSystem.payment_options,
        true
      )
    }

    const effectiveProposalTemplateSettings = buildEffectiveProposalTemplateSettings(
      proposalTemplateSettings,
      selectedProject
    )

    const proposalDataQuoteLineItems = hydratedSelectedSystem?.userData?.quote_line_items
    const enrichedSelectedProject = enrichProjectProposalDataWithQuoteLineItems(
      selectedProject,
      proposalDataQuoteLineItems
    )

    const fallbackCountryIso2 = selectedProject?.country_iso2 || org?.country_iso2 || null
    const merged = mergeNonSolarQuoteSystemIntoProposal({
      selectedProject: enrichedSelectedProject,
      selectedSystem: hydratedSelectedSystem,
      systems: hydratedSystems,
    })
    const hydratedPaymentOptionMerged = enrichPaymentOptionWithProposalContractFields(
      hydratePaymentOptionForProposal(hydratedPaymentOption ?? merged.selectedSystem?.payment_options?.[0]),
      merged.selectedProject,
      effectiveProposalTemplateSettings
    )

    return {
      selectedPaymentOption: hydratedPaymentOptionMerged,
      proposalTemplateSettings: effectiveProposalTemplateSettings,
      org: org,
      selectedProject: merged.selectedProject,
      systems: merged.systems,
      selectedSystem: merged.selectedSystem,
      otherDesigns: otherDesigns || [],
      otherAccounts: otherAccounts || [],
      viewUuids: viewUuids || [],
      selectedViewUuid: selectedViewUuid,
      selectedPaymentOptionId: hydratedPaymentOption?.id ?? selectedPaymentOptionId,
      myeStyles: myeStyles,
      fullCalcReady: fullCalcReady ?? true,
      currencySymbol: currencySymbolForCountry(fallbackCountryIso2),
      solarOutputAsPercentage: 0,
      currentSolarOutputAsPercentage: 0,
      cashFlows: { bank_balance: [], cash_flows: [] },
      YAxisRangeAllSystemsAndPaymentOptions: {
        bank_balance: { maxYAxis: 0, minYAxis: 0 },
        cash_flows: { maxYAxis: 0, minYAxis: 0 },
        bill_savings: { maxYAxis: 0, minYAxis: 0 },
      },
      checkoutHidden: proposalTemplateSettings?.proposal_checkout_buttons === 'disabled',
      isExpired: false,
      changeOrderProposal: changeOrderProposal,
      is_retrofit_project: !!selectedProject?.proposal_data?.is_retrofit_project,
    }
  }

  // Use country on design if set, otherwise fallback to org country
  const countryIso2 =
    selectedProject && selectedProject.country_iso2 && selectedProject.country_iso2.length > 0
      ? selectedProject.country_iso2
      : org
        ? org.country_iso2
        : null

  const currencySymbol = currencySymbolForCountry(countryIso2)

  const selectedPaymentOption =
    selectedSystem?.payment_options && selectedSystem.payment_options.length
      ? getPaymentOptionById(selectedPaymentOptionId, selectedSystem.payment_options, true)
      : null

  //@TODO: Fix this hack: Promote the relevant    bills['utility_tariff_id'] => bills    for selected payment option
  //We must detect selectedPaymentOption first so we can detect utility_tariff_id to select the correct proposed bills
  systems.forEach((s) => {
    //Inject the bills for proposed/selected utility tariff
    //
    //It's ok if the utility_tariff_id we are looking for does not exist (e.g. if using dummy value of -1)
    //below we will simply use the first entry if not found.
    var proposedSelected = s.bills.proposed[selectedPaymentOption?.utility_tariff_id]

    //If no match found or selectedPaymentOption.utility_tariff_id not set, then just use first bill proposed
    if (!proposedSelected) {
      console.warn('selectedPaymentOption.utility_tariff_id not found, using first proposed bill instead.')
      proposedSelected = s.bills.proposed[Object.keys(s.bills.proposed)[0]]
    }

    s.bills.proposedSelected = proposedSelected

    // strip out any V1 phoenix payment options, these should never be displayed in the payment option selection buttons/dropdown
    s.payment_options = s.payment_options?.filter((pmt) => {
      if (['phoenix'].includes(pmt?.integration) && !getIsPhoenixV2(pmt.integration_external_reference)) {
        return false
      }
      return true
    })
  })

  const cashFlows = {
    bank_balance:
      selectedPaymentOption && selectedPaymentOption['bank_balance'] && selectedPaymentOption['bank_balance'].length
        ? selectedPaymentOption['bank_balance']
        : [],
    cash_flows:
      selectedPaymentOption && selectedPaymentOption['cash_flows'] && selectedPaymentOption['cash_flows'].length
        ? selectedPaymentOption['cash_flows']
        : [],
  }

  const YAxisRangeAllSystemsAndPaymentOptions = {
    bank_balance: getCashFlowAxisRangeAllSystemsAndPaymentOptions(systems, 'bank_balance'),
    cash_flows: getCashFlowAxisRangeAllSystemsAndPaymentOptions(systems, 'cash_flows'),
    bill_savings: getBillSavingsAxisRangeAllSystemsAndPaymentOptions(systems),
  }

  const solarOutputAsPercentage =
    !selectedSystem || !selectedSystem.consumption || !selectedSystem.consumption.consumption_offset_percentage
      ? 0
      : selectedSystem.consumption.consumption_offset_percentage

  const currentSolarOutputAsPercentage = selectedSystem?.consumption?.current?.consumption_offset_percentage ?? 0

  const proposal_checkout_buttons = proposalTemplateSettings?.proposal_checkout_buttons
  const date = new Date()
  const is_proposal_expired = date.getTime() > Date.parse(selectedProject.valid_until_date)
  const checkoutHidden =
    proposal_checkout_buttons === 'disabled' ||
    (proposal_checkout_buttons === 'disable_after_expiry' && is_proposal_expired)

  let isExpired = false
  if (
    proposalTemplateSettings?.proposal_checkout_buttons === 'disable_after_expiry' &&
    selectedProject?.valid_until_date
  ) {
    const validUntilDate = Date.parse(selectedProject?.valid_until_date)
    // Consider proposal expired when valid_until_date is in the past (client local date).
    if (!isNaN(validUntilDate) && validUntilDate < new Date()) {
      isExpired = true
    }
  }

  const merged = mergeNonSolarQuoteSystemIntoProposal({
    selectedProject,
    selectedSystem,
    systems,
  })
  const effectiveTemplateSettings = buildEffectiveProposalTemplateSettings(
    proposalTemplateSettings,
    merged.selectedProject
  )
  const hydratedPaymentOption = enrichPaymentOptionWithProposalContractFields(
    hydratePaymentOptionForProposal(selectedPaymentOption ?? merged.selectedSystem?.payment_options?.[0]),
    merged.selectedProject,
    effectiveTemplateSettings
  )

  const templateLanguage = effectiveTemplateSettings?.proposal_v2_config?.template_settings?.language
  const locale = resolveProposalDisplayLocale({
    templateLanguage,
    projectLanguage: merged.selectedProject?.language_override || merged.selectedProject?.language,
  })

  const promotionalPricing =
    selectedSystem?.uuid && selectedProject?.proposal_data?.systems
      ? (selectedProject.proposal_data.systems.find((s) => s?.data?.uuid === selectedSystem.uuid)
          ?.promotional_pricing ?? null)
      : null

  return {
    selectedPaymentOption: hydratedPaymentOption,
    proposalTemplateSettings: effectiveTemplateSettings,
    org: org,
    selectedProject: {
      ...merged.selectedProject,
      testimonials: merged.selectedProject?.testimonials ?? org?.testimonials ?? [],
    },
    systems: merged.systems,
    selectedSystem: merged.selectedSystem,
    otherDesigns: otherDesigns,
    otherAccounts: otherAccounts,
    viewUuids: viewUuids,
    selectedViewUuid: selectedViewUuid,
    selectedPaymentOptionId: selectedPaymentOptionId,
    myeStyles: myeStyles,
    fullCalcReady,
    //inject extras too
    currencySymbol: currencySymbol,
    locale,
    solarOutputAsPercentage: solarOutputAsPercentage,
    currentSolarOutputAsPercentage: currentSolarOutputAsPercentage,
    cashFlows: cashFlows,
    YAxisRangeAllSystemsAndPaymentOptions: YAxisRangeAllSystemsAndPaymentOptions,
    checkoutHidden: checkoutHidden,
    isExpired: isExpired,
    changeOrderProposal: changeOrderProposal,
    is_retrofit_project: !!selectedProject.proposal_data?.is_retrofit_project,
    promotionalPricing,
  }
}

export const showCoverMessageDialog = (proposalData) => {
  const showLetter =
    proposalData && getTemplateSettings(proposalData.proposalTemplateSettings, 'sections').indexOf('letter') !== -1
  const usingPVProSellTemplate =
    proposalData &&
    getTemplateSettings(proposalData.proposalTemplateSettings, 'sections').indexOf('iframe_with_post_data') !== -1 &&
    !!proposalData.proposalTemplateSettings?.pvprosell_template_id

  if (usingPVProSellTemplate) {
    // hide the cover message dialog when no cover message is added
    if (showLetter && proposalData?.selectedProject?.proposal_data?.proposal_message?.length > 0) {
      return true
    } else {
      return false
    }
  } else {
    // always show cover message dialog for OpenSolar template even there is no cover message added
    if (showLetter && proposalData?.selectedProject?.proposal_data?.hasOwnProperty('proposal_message')) {
      return true
    } else {
      return false
    }
  }
}

export const letterContentIsEmpty = (proposalData) => {
  const showLetter =
    proposalData && getTemplateSettings(proposalData.proposalTemplateSettings, 'sections').indexOf('letter') !== -1
  if (
    showLetter &&
    proposalData &&
    proposalData.selectedProject &&
    proposalData.selectedProject.proposal_data &&
    proposalData.selectedProject.proposal_data.proposal_message === ''
  ) {
    return true
  } else {
    return false
  }
}

export const letterIsAvailable = (proposalData) => {
  const showLetter =
    proposalData && getTemplateSettings(proposalData.proposalTemplateSettings, 'sections').indexOf('letter') !== -1
  if (
    showLetter &&
    proposalData &&
    proposalData.selectedProject &&
    proposalData.selectedProject.proposal_data &&
    proposalData.selectedProject.proposal_data.proposal_message &&
    proposalData.selectedProject.proposal_data.proposal_message.length > 0
  ) {
    return true
  } else {
    return false
  }
}
